import React, { useState, useEffect, useRef } from 'react';
import { API_URL, LOCATION_ID } from '..';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';

const ProfessionalSwitch = styled(Switch)(() => ({
  width: 46,
  height: 26,
  padding: 0,
  display: 'flex',

  '& .MuiSwitch-switchBase': {
    padding: 3,
    transitionDuration: '200ms',

    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',

      '& + .MuiSwitch-track': {
        backgroundColor: '#2563eb',
        opacity: 1,
      },
    },
  },

  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.25)',
  },

  '& .MuiSwitch-track': {
    borderRadius: 13,
    backgroundColor: '#cbd5e1',
    opacity: 1,
    boxSizing: 'border-box',
  },
}));

const SectionTitle = ({ title }) => (
  <div className="stock-section-title">
    <h6>{title}</h6>
  </div>
);

const SettingCard = ({ title, description, checked, onChange }) => (
  <div className="stock-setting-card">
    <div>
      <p className="stock-setting-title">{title}</p>
      <p className="stock-setting-desc">{description}</p>
    </div>

    <ProfessionalSwitch
      checked={checked}
      onChange={onChange}
      inputProps={{ 'aria-label': title }}
    />
  </div>
);

const AddStockLayer = () => {
  const [formValues, setFormValues] = useState({
    title: '',
    sellPrice: 0,
    costPrice: 0,
    quantity: 0,
    description: '',
    locationId: LOCATION_ID,
    sku: '',
    color:'',
    productName: '',
    itemName: '',
    trackInventory: true,
    allowOutOfStockPurchases: false,
    image: '',
    ghlProductId: '',
    ghlId: '',
  });

  const [message, setMessage] = useState({
    status: false,
    reason: '',
    desc: '',
  });
  const [colorList, setColorList] = useState([]);

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  useEffect(() => {
    const fetchColors = async () => {
      try {
        const res = await fetch(`${API_URL}/api/color`);
        const data = await res.json();
        setColorList(data);
      } catch (error) {
        console.error('Failed to fetch colors:', error);
      }
    };
    fetchColors();
  }, []);

  useEffect(() => {
    if (quillRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
          ],
        },
        placeholder: 'Enter stock description',
      });

      quillInstance.current.root.setAttribute('dir', 'ltr');
      quillInstance.current.root.setAttribute('lang', 'en');

      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        setFormValues((prev) => ({ ...prev, description: content }));
      });
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change');
        quillInstance.current = null;
      }
    };
  }, []);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormValues({
      title: '',
      sellPrice: 0,
      costPrice: 0,
      quantity: 0,
      description: '',
      locationId: LOCATION_ID,
      sku: '',
      productName: '',
      itemName: '',
      trackInventory: true,
      allowOutOfStockPurchases: false,
      image: '',
      ghlProductId: '',
      ghlId: '',
    });

    if (quillInstance.current) {
      quillInstance.current.root.innerHTML = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem('user'));

    if (!user) {
      setMessage({
        status: true,
        reason: 'error',
        desc: 'User not authenticated. Please sign in.',
      });
      return;
    }

    if (!formValues.title) {
      return setMessage({
        status: true,
        reason: 'error',
        desc: 'Title is required.',
      });
    }

    if (!formValues.costPrice || Number(formValues.costPrice) <= 0) {
      return setMessage({
        status: true,
        reason: 'error',
        desc: 'Buy price must be greater than 0.',
      });
    }

    if (!formValues.sellPrice || Number(formValues.sellPrice) <= 0) {
      return setMessage({
        status: true,
        reason: 'error',
        desc: 'Selling price must be greater than 0.',
      });
    }

    if (!formValues.quantity || Number(formValues.quantity) <= 0) {
      return setMessage({
        status: true,
        reason: 'error',
        desc: 'Quantity must be greater than 0.',
      });
    }

    if (!formValues.description || formValues.description === '<p><br></p>') {
      return setMessage({
        status: true,
        reason: 'error',
        desc: 'Description is required.',
      });
    }

    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          status: true,
          reason: 'success',
          desc: 'Stock item added successfully.',
        });
        resetForm();
      } else {
        throw new Error(data.message || 'Failed to add stock.');
      }
    } catch (error) {
      setMessage({
        status: true,
        reason: 'error',
        desc: `Failed to add stock: ${error.message}`,
      });
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      <style>
        {`
          .stock-form-card {
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            overflow: hidden;
            background: #ffffff;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
          }

          .stock-form-header {
            padding: 18px 22px;
            border-bottom: 1px solid #e5e7eb;
            background: #ffffff;
          }

          .stock-form-header h6 {
            font-size: 18px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 4px;
          }

          .stock-form-header p {
            font-size: 13px;
            color: #64748b;
            margin-bottom: 0;
          }

          .stock-badge {
            font-size: 11px;
            font-weight: 600;
            color: #2563eb;
            background: #eff6ff;
            border: 1px solid #bfdbfe;
            padding: 6px 10px;
            border-radius: 999px;
          }

          .stock-section-title {
            margin: 20px 0 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid #eef2f7;
          }

          .stock-section-title h6 {
            font-size: 12px;
            font-weight: 700;
            color: #111827;
            text-transform: uppercase;
            letter-spacing: .04em;
            margin-bottom: 0;
          }

          .stock-setting-card {
            min-height: 74px;
            padding: 14px 16px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            transition: all 0.2s ease;
          }

          .stock-setting-card:hover {
            border-color: #bfdbfe;
            box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
          }

          .stock-setting-title {
            font-size: 13px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 2px;
          }

          .stock-setting-desc {
            font-size: 11px;
            color: #64748b;
            margin-bottom: 0;
            line-height: 1.4;
          }

          .stock-form-card .form-control {
            min-height: 40px;
            border-color: #dbe2ea;
            color: #111827;
            font-size: 13px;
          }

          .stock-form-card .form-control:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.10);
          }

          .stock-form-card .form-label {
            color: #1f2937;
            font-size: 12px;
            margin-bottom: 6px;
          }

          .stock-form-card .form-text {
            font-size: 11px;
            color: #64748b;
            margin-top: 6px;
          }

          .ql-toolbar.ql-snow {
            border-color: #dbe2ea;
            border-radius: 9px 9px 0 0;
            background: #f8fafc;
            padding: 6px 8px;
          }

          .ql-container.ql-snow {
            min-height: 125px;
            border-color: #dbe2ea;
            border-radius: 0 0 9px 9px;
          }

          .ql-editor {
            min-height: 125px;
            font-size: 13px;
          }

          .stock-action-bar {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            padding-top: 16px;
            margin-top: 18px;
            border-top: 1px solid #e5e7eb;
          }

          @media (max-width: 767px) {
            .stock-action-bar {
              flex-direction: column;
            }

            .stock-action-bar button {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-8 col-xl-9 col-lg-11">
            <div className="stock-form-card">
              <div className="stock-form-header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div>
                    <h6>Add New Stock Item</h6>
                    <p>Add item details, pricing, and stock controls.</p>
                  </div>

                  <span className="stock-badge">Inventory</span>
                </div>
              </div>

              <div className="card-body p-20">
                {message.status && (
                  <div
                    className={`alert alert-${
                      message.reason === 'error' ? 'danger' : 'success'
                    } alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() =>
                        setMessage({ status: false, reason: '', desc: '' })
                      }
                      aria-label="Close"
                    />
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <SectionTitle title="Basic Information" />

                  <div className="row">
                    <div className="col-md-6 mb-16">
                      <label htmlFor="title" className="form-label fw-semibold">
                        Title <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="title"
                        value={formValues.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Short stock title"
                      />
                    </div>

                    <div className="col-md-6 mb-16">
                      <label htmlFor="sku" className="form-label fw-semibold">
                        SKU
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="sku"
                        value={formValues.sku}
                        onChange={(e) => handleChange('sku', e.target.value)}
                        placeholder="e.g. CG-PVC-300MM"
                      />
                    </div>
                    

                    <div className="col-md-6 mb-16">
                      <label htmlFor="productName" className="form-label fw-semibold">
                        Product Name
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="productName"
                        value={formValues.productName}
                        onChange={(e) => handleChange('productName', e.target.value)}
                        placeholder="Full product name"
                      />
                    </div>

                    <div className="col-md-6 mb-16">
                      <label htmlFor="color" className="form-label fw-semibold">
                        COLOR
                      </label>
                      <select
                        className="form-select radius-8"
                        id="color"
                        value={formValues.color}
                        onChange={(e) => handleChange('color', e.target.value)}
                      >
                        <option value="">Select Color</option>
                        {colorList.map((color) => (
                          <option key={color.id} value={color.title}>{color.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-16">
                      <label htmlFor="itemName" className="form-label fw-semibold">
                        Variant Name
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="itemName"
                        value={formValues.itemName}
                        onChange={(e) => handleChange('itemName', e.target.value)}
                        placeholder="Variant description"
                      />
                    </div>

                    <div className="col-md-12 mb-16">
                      <label htmlFor="image" className="form-label fw-semibold">
                        Image URL
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="image"
                        value={formValues.image}
                        onChange={(e) => handleChange('image', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  {/* <SectionTitle title="GHL References" />

                  <div className="row">
                    <div className="col-md-6 mb-16">
                      <label htmlFor="ghlProductId" className="form-label fw-semibold">
                        GHL Product ID
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="ghlProductId"
                        value={formValues.ghlProductId}
                        onChange={(e) => handleChange('ghlProductId', e.target.value)}
                        placeholder="Product field ID"
                      />
                    </div>

                    <div className="col-md-6 mb-16">
                      <label htmlFor="ghlId" className="form-label fw-semibold">
                        GHL Item ID
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="ghlId"
                        value={formValues.ghlId}
                        onChange={(e) => handleChange('ghlId', e.target.value)}
                        placeholder="Item field ID"
                      />
                    </div>
                  </div> */}

                  <SectionTitle title="Pricing" />

                  <div className="row">
                    <div className="col-md-4 mb-16">
                      <label htmlFor="buy" className="form-label fw-semibold">
                        Buy Price <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="buy"
                        value={formValues.costPrice}
                        onChange={(e) => handleChange('costPrice', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-md-4 mb-16">
                      <label htmlFor="sell" className="form-label fw-semibold">
                        Selling Price <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="sell"
                        value={formValues.sellPrice}
                        onChange={(e) => handleChange('sellPrice', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-md-4 mb-16">
                      <label htmlFor="qty" className="form-label fw-semibold">
                        Quantity <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="qty"
                        value={formValues.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                        min="1"
                        placeholder="Qty"
                      />
                    </div>
                  </div>

                  <SectionTitle title="Inventory Settings" />

                  <div className="row">
                    <div className="col-md-6 mb-16">
                      <SettingCard
                        title="Track Inventory"
                        description="Monitor stock quantity."
                        checked={formValues.trackInventory}
                        onChange={(e) =>
                          handleChange('trackInventory', e.target.checked)
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-16">
                      <SettingCard
                        title="Out-of-Stock Sales"
                        description="Allow sales at zero stock."
                        checked={formValues.allowOutOfStockPurchases}
                        onChange={(e) =>
                          handleChange('allowOutOfStockPurchases', e.target.checked)
                        }
                      />
                    </div>
                  </div>

                  <SectionTitle title="Description" />

                  <div className="mb-16">
                    <label htmlFor="description" className="form-label fw-semibold">
                      Description <span className="text-danger-600">*</span>
                    </label>

                    <div
                      ref={quillRef}
                      className={
                        message.reason === 'error' &&
                        (!formValues.description ||
                          formValues.description === '<p><br></p>')
                          ? 'is-invalid'
                          : ''
                      }
                    />

                    <div className="form-text">
                      Supports bold text, alignment, and bullet lists.
                    </div>
                  </div>

                  <div className="stock-action-bar">
                    <button
                      type="button"
                      className="btn btn-light border text-sm px-24 py-9 radius-8"
                      onClick={resetForm}
                    >
                      Reset
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-400 text-sm px-32 py-9 radius-8 shadow-sm"
                    >
                      Add Stock
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStockLayer;